import type {
  AsyncTask,
  AsyncTaskLifecyclePhase,
  PinTaskHints,
  RegisterConfig,
} from "../types";
import {
  isKnownAsyncTaskTypeId,
  statusToLifecyclePhase,
} from "../utils";

interface Subscriber {
  id: number;
  config: RegisterConfig;
}

export interface AsyncTaskStore {
  register: (config: RegisterConfig) => () => void;
  applySnapshot: (tasks: AsyncTask[]) => void;
  /** Drop all tasks/pins so a later login does not flash the previous session. */
  clear: () => void;
  pinTask: (taskId: number, hints?: PinTaskHints) => void;
  getTasks: () => AsyncTask[];
  getSubscriberCount: () => number;
  hasPendingTasks: () => boolean;
  subscribe: (listener: () => void) => () => void;
}

export function createAsyncTaskStore(): AsyncTaskStore {
  const tasks = new Map<number, AsyncTask>();
  const lastPhase = new Map<number, AsyncTaskLifecyclePhase>();
  const pinnedIds = new Set<number>();
  /** Real task ids pinned as pending before first poll confirms status. */
  const pinnedPendingIds = new Set<number>();
  const subscribers = new Map<number, Subscriber>();
  const listeners = new Set<() => void>();

  let nextSubscriberId = 1;
  let tasksSnapshot: AsyncTask[] = [];

  const rebuildTasksSnapshot = () => {
    tasksSnapshot = Array.from(tasks.values()).sort((a, b) => b.id - a.id);
  };

  const notify = () => {
    rebuildTasksSnapshot();
    for (const listener of listeners) listener();
  };

  const emitPhase = (task: AsyncTask, phase: AsyncTaskLifecyclePhase) => {
    for (const { config } of subscribers.values()) {
      if (!config.filter(task)) continue;

      switch (phase) {
        case "pending":
          config.onPending?.(task);
          break;
        case "completed":
          config.onCompleted?.(task);
          break;
        case "failed":
          config.onFailed?.(task);
          break;
        case "cancelled":
          config.onCancelled?.(task);
          break;
      }
    }
  };

  const handleTransition = (task: AsyncTask) => {
    const id = task.id;
    const phase = statusToLifecyclePhase(task.status);
    const prev = lastPhase.get(id);

    let emitted = false;

    if (prev === undefined) {
      if (phase === "pending") {
        emitPhase(task, phase);
        emitted = true;
      } else if (pinnedPendingIds.has(id)) {
        // Fast-completing or poll-gap: pinned pending but first snapshot is terminal.
        emitPhase(task, phase);
        emitted = true;
        pinnedPendingIds.delete(id);
      }
    } else if (prev !== phase) {
      emitPhase(task, phase);
      emitted = true;
      pinnedPendingIds.delete(id);
    }

    if (emitted && phase !== "pending") {
      pinnedPendingIds.delete(id);
    }

    lastPhase.set(id, phase);
  };

  const register = (config: RegisterConfig) => {
    const id = nextSubscriberId++;
    subscribers.set(id, { id, config });
    notify();

    return () => {
      subscribers.delete(id);
      notify();
    };
  };

  const mergeTaskSnapshot = (existing: AsyncTask | undefined, incoming: AsyncTask): AsyncTask => {
    if (!existing) return incoming;

    return {
      ...incoming,
      meta_data: {
        ...(existing.meta_data as object),
        ...(incoming.meta_data as object),
      } as AsyncTask["meta_data"],
    };
  };

  const applySnapshot = (incoming: AsyncTask[]) => {
    const incomingIds = new Set(incoming.map((t) => t.id));

    for (const task of incoming) {
      const merged = mergeTaskSnapshot(tasks.get(task.id), task);
      tasks.set(task.id, merged);
      pinnedIds.delete(task.id);
      handleTransition(merged);
    }

    for (const id of [...tasks.keys()]) {
      if (!incomingIds.has(id) && !pinnedIds.has(id)) {
        tasks.delete(id);
        lastPhase.delete(id);
        pinnedPendingIds.delete(id);
      }
    }

    notify();
  };

  const clear = () => {
    tasks.clear();
    lastPhase.clear();
    pinnedIds.clear();
    pinnedPendingIds.clear();
    notify();
  };

  const pinTask = (taskId: number, hints?: PinTaskHints) => {
    if (!Number.isFinite(taskId) || taskId <= 0) {
      notify();
      return;
    }

    pinnedIds.add(taskId);
    pinnedPendingIds.add(taskId);

    const existing = tasks.get(taskId);
    const type = hints?.type ?? existing?.type;

    if (type == null || !isKnownAsyncTaskTypeId(type)) {
      notify();
      return;
    }

    const meta = hints?.meta_data
      ? ({
          ...(existing?.meta_data as object),
          ...hints.meta_data,
        } as AsyncTask["meta_data"])
      : (existing?.meta_data ?? ({} as AsyncTask["meta_data"]));

    const optimistic: AsyncTask = {
      id: taskId,
      type,
      status: "Pending",
      meta_data: meta,
      is_seen: existing?.is_seen ?? false,
      is_retrieved: existing?.is_retrieved ?? false,
      created_at: existing?.created_at,
      updated_at: existing?.updated_at,
    };

    tasks.set(taskId, optimistic);
    handleTransition(optimistic);
    notify();
  };

  const getTasks = (): AsyncTask[] => tasksSnapshot;

  const getSubscriberCount = () => subscribers.size;

  const hasPendingTasks = () => {
    if (pinnedIds.size > 0) return true;
    for (const task of tasks.values()) {
      if (task.status === "Pending") return true;
    }
    return false;
  };

  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  return {
    register,
    applySnapshot,
    clear,
    pinTask,
    getTasks,
    getSubscriberCount,
    hasPendingTasks,
    subscribe,
  };
}
